import { CanvasService } from "appworks/graphics/canvas/canvas-service";
import { ButtonElement, ButtonEvent } from "appworks/graphics/elements/button-element";
import { Layers } from "appworks/graphics/layers/layers";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { Model } from "appworks/model/model";
import { Services } from "appworks/services/services";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { uiFlags, UIMenuFlag } from "appworks/ui/flags/ui-flags";
import { LayerNames } from "game-layers";
import { gameiomModel } from "gameiom/model/gameiom-model";
import { HomeCommand } from "slotworks/commands/betbar/home-command";
import { AbstractControl } from "../../abstract-control";
import { UISettingsButtons } from "gameiom/wrapper/wrapper-data";
import { RenderOrientation } from "appworks/graphics/pixi/render-orientation";
import { SoundService } from "appworks/services/sound/sound-service";

/**
 * Interface representing a settings menu button.
 */
interface ISettingsMenuButton {
    name: string;
    translationKey: string;
    clickCallback: () => void;
}

/**
 * Class representing a settings menu control.
 */
export class SettingsMenuControl extends AbstractControl {
    // Map of all available settings button options.
    protected static readonly allSettingsButtons: Map<string, ISettingsMenuButton> = new Map();

    // Pixel buffer above the top button and below the bottom button.
    protected readonly verticalBuffer: number = 40;

    // Pixel gap between buttons.
    protected readonly pixelsBetweenButtons: number = 15;

    // Name for the background sprite.
    protected readonly menuBackgroundName: string = "menuBackground";

    // Callback when orientation change is dispatched.
    protected orientationChangeCallback: Function;

    /**
     * Initializes all settings buttons.
     */
    public static init(): void {
        SettingsMenuControl.allSettingsButtons.set(UISettingsButtons.RULES_BUTTON, {
            name: UISettingsButtons.RULES_BUTTON,
            translationKey: "btn.hel",
            clickCallback: () => {
              uiFlags.menu(UIMenuFlag.PAYTABLE);
            }
        });
          SettingsMenuControl.allSettingsButtons.set(UISettingsButtons.DEPOSIT_BUTTON, {
            name: UISettingsButtons.DEPOSIT_BUTTON,
            translationKey: "btn.dep",
            clickCallback: () => {
              gameWrapper.gotoDepositPage();
            }
        });
          SettingsMenuControl.allSettingsButtons.set(UISettingsButtons.ACCOUNT_BUTTON, {
            name: UISettingsButtons.ACCOUNT_BUTTON,
            translationKey: "btn.acc",
            clickCallback: () => {
              gameWrapper.gotoAccountsPage();
            }
        });
          SettingsMenuControl.allSettingsButtons.set(UISettingsButtons.HISTORY_BUTTON, {
            name: UISettingsButtons.HISTORY_BUTTON,
            translationKey: "btn.his",
            clickCallback: () => {
              console.log("history clicked");
            }
        });
          SettingsMenuControl.allSettingsButtons.set(UISettingsButtons.QUIT_BUTTON, {
            name: UISettingsButtons.QUIT_BUTTON,
            translationKey: "btn.qui",
            clickCallback: () => {
              const { homeURL } = Model.read().settings;
              HomeCommand(homeURL, () => gameWrapper.exitGame()).execute();
            }
        });
    }

    /**
     * Constructor for the SettingsMenuControl class.
     * @param {ButtonElement} genericButton - A generic button element.
     * @param {Sprite} backgroundSprite - The menu background sprite.
     */
    constructor(protected genericButton: ButtonElement, protected backgroundSprite: Sprite) {
        super();

        this.orientationChangeCallback = () => {
            this.removeDynamicSettingsRefs();
            this.regenerateMenu();
        };

        Services.get(CanvasService).onOrientationChange.add(this.orientationChangeCallback, this);

        this.regenerateMenu();
    }

    /**
     * Sets the enabled state of the control. This does not get disabled.
     * It will get destroyed when it's no longer used.
     * @param {boolean} _ - Unused/discard.
     */
    public override setEnabled(_: boolean): void {
        // Never disable
    }

    /**
     * Called when the control is destroyed.
     */
    public override destroy(): void {
        this.removeDynamicSettingsRefs();
        Services.get(CanvasService).onOrientationChange.remove(this.orientationChangeCallback, this);
    }

    /**
     * Regenerates the settings menu from scratch by removing existing elements from the MenusContent layer
     * then adding the dynamic elements.
     */
    protected regenerateMenu(): void {
        const orientation = RenderOrientation.orientation;

        // Remove existing MenusContent elements, except for the close button.
        const menusContentLayer = Layers.get(LayerNames.MenusContent);
        const closeButton = this.removeMenusContentRefs(menusContentLayer);

        // Find the center point of the canvas.
        const canvasService = Services.get(CanvasService);
        const background = new Sprite(this.backgroundSprite.texture);
        const clonedBackgroundDualPos = background.dualPosition.clone();
        const canvasCenterPoint = new PIXI.Point(canvasService.stageWidth / 2, canvasService.stageHeight / 2);

        // Set the menu background to the center and add it to the layer.
        clonedBackgroundDualPos[orientation].x = canvasCenterPoint.x;
        clonedBackgroundDualPos[orientation].y = canvasCenterPoint.y;
        background.setDualPosition(clonedBackgroundDualPos);
        background.anchor.set(0.5);
        background.name = this.menuBackgroundName;
        background.interactive = true;
        menusContentLayer.add(background);

        // Create buttons and fit background based on total buttons height.
        this.addButtonsAndFitBackground(this.genericButton, background, closeButton, canvasCenterPoint, menusContentLayer);
    }

    /**
     * Removes all references to standard menus content elements except the 'close' button and invisible cover.
     * @param {Layers} menusContentLayer - The MenusContent layer.
     * @returns The close button.
     */
    protected removeMenusContentRefs(menusContentLayer: Layers): ButtonElement {
        let closeButton: ButtonElement;

        for(const key in menusContentLayer.allElements) {
            // Close button is being removed, but we also want to return the reference to it so that we can re-add it, thus forcing its z-value to
            // be above the other elements in our dynamic menu.
            if(key === 'close' && menusContentLayer.allElements.hasOwnProperty(key)) {
                closeButton = menusContentLayer.allElements[key];
            }

            // Invisible cover stays for detecting clicks off menu
            if(key === 'cover' && menusContentLayer.allElements.hasOwnProperty(key)) {
                const cover = menusContentLayer.allElements[key] as Sprite;

                // Only add one click listener even if orientation changes.
                if(cover.listenerCount('pointertap') == 0) {
                    cover.interactive = true;

                    cover.on('pointertap', () => {
                        Services.get(SoundService).customEvent("button_press_close");
                        uiFlags.menu(0);
                    });
                }

                continue;
            }

            // Everything else must go.
            if(menusContentLayer.allElements.hasOwnProperty(key)) {
                menusContentLayer.remove(menusContentLayer.allElements[key], key);
            }
        }

        return closeButton;
    }

    /**
     * Removes all references to dynamic settings buttons and the menu background sprite.
     */
    protected removeDynamicSettingsRefs(): void {
        const menusContentLayer = Layers.get(LayerNames.MenusContent);

        // Remove all dynamic settings buttons.
        for(const key in SettingsMenuControl.allSettingsButtons.keys) {
            if(menusContentLayer.allElements.hasOwnProperty(key)) {
                menusContentLayer.remove(menusContentLayer.allElements[key], key);
            }
        }

        // Remove the menu background sprite.
        if(menusContentLayer.allElements.hasOwnProperty(this.menuBackgroundName)) {
            menusContentLayer.remove(menusContentLayer.allElements[this.menuBackgroundName], this.menuBackgroundName);
        }
    }

    /**
     * Adds the enabled settings buttons to the menus content layer and fits the background sprite to the buttons.
     * @param {ButtonElement} genericButton - A generic button element.
     * @param {Sprite} background - The menu background sprite.
     * @param {ButtonElement} closeButton - The close button.
     * @param {PIXI.Point} canvasCenter - The center point of the canvas.
     * @param {Layers} menusContentLayer - The MenusContent layer.
     */
    protected addButtonsAndFitBackground(genericButton: ButtonElement, background: Sprite, closeButton: ButtonElement, canvasCenter: PIXI.Point, menusContentLayer: Layers): void {
        const orientation = RenderOrientation.orientation;
        const backgroundRect = background.dualPosition[orientation];
        const backgroundTrueLeft = backgroundRect.x - (backgroundRect.width / 2);
        const numButtons = gameiomModel.enabledSettingsButtons.length;

        let nextButtonY = -1;

        // Generate buttons for each enabled settings button in the gameiom model.
        gameiomModel.enabledSettingsButtons.forEach((eachButton) => {
            // Only pre-defined settings are allowed to be configured.
            if(!SettingsMenuControl.allSettingsButtons.has(eachButton)) {
                return;
            }

            // Clone the generic button and add appropriate callback.
            const clonedButton: ButtonElement = genericButton.clone();
            const menuButton = SettingsMenuControl.allSettingsButtons.get(eachButton);
            clonedButton.on(ButtonEvent.CLICK, menuButton.clickCallback);
            const buttonRect = clonedButton.dualPosition[orientation];

            // Set the initial/top button placement, close button placement, and size the menu.
            if(nextButtonY < 0) {
                const totalButtonHeight = this.getTotalButtonHeight(buttonRect.height, numButtons);
                const backgroundHeight = (this.verticalBuffer * 2) + totalButtonHeight;
                background.dualPosition[orientation].height = backgroundHeight;
                nextButtonY = this.getInitialButtonY(totalButtonHeight, canvasCenter.y);
                closeButton.dualPosition[orientation].x = (backgroundRect.x + (backgroundRect.width / 2)) - (closeButton.dualPosition[orientation].width / 2);
                closeButton.dualPosition[orientation].y = (backgroundRect.y - (backgroundRect.height / 2)) - (closeButton.dualPosition[orientation].height / 2);

                nextButtonY += this.pixelsBetweenButtons;
            }

            // Generic button should have one label attached. Ensure label text matches the defined translation.
            clonedButton.getLabels().forEach(eachLabel => {
                eachLabel.text = Services.get(TranslationsService).get(menuButton.translationKey);
            });

            // Position the button appropriately.
            const buttonLeft = backgroundTrueLeft + ((backgroundRect.width - buttonRect.width) / 2);
            buttonRect.x = buttonLeft;
            buttonRect.y = nextButtonY;

            // Button name should match defined settings button options.
            clonedButton.name = eachButton;

            // Add button properly to the layer.
            menusContentLayer.add(clonedButton);
            menusContentLayer.addButton(eachButton, clonedButton);

            // Set the starting point for the next button placement.
            nextButtonY += buttonRect.height + this.pixelsBetweenButtons;
        });

        menusContentLayer.add(closeButton);
    }

    /**
     * Calculates the total height of all the buttons in the menu with gaps.
     * @param {number} buttonHeight The height of each button in the menu.
     * @param {number} numButtons The total number of buttons in the menu.
     * @returns The total height of all the buttons in the menu with gaps.
     */
    protected getTotalButtonHeight(buttonHeight: number, numButtons: number): number {
        const numGaps = numButtons - 1;

        return (numButtons * buttonHeight) + (numGaps * this.pixelsBetweenButtons);
    }

    /**
     * Calculates the y-coordinate of the topmost button in the menu, based on the total height of the menu and the
     * vertical center of the canvas.
     * @param totalButtonHeight The total height of all the buttons in the menu with gaps.
     * @param canvasCenterY The vertical center of the canvas.
     * @returns The y-coordinate of the topmost button in the menu.
     */
    protected getInitialButtonY(totalButtonHeight: number, canvasCenterY: number): number {
        return canvasCenterY - (totalButtonHeight / 2);
    }
}

SettingsMenuControl.init();